import { AiResultContainer, AppId, equalInLC, FCX, sanitize, usePlatformContext } from "@datassential/platform-ui-lib";
import HomeSectionHeader from "@pages/Home/components/HomeSectionHeader";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import { useHomeCategoryReportsSummary } from "@apiHooks/useHomeCategoryReportsSummary";
import Skeleton from "@components/Skeleton";
import './HomeCategoryReportsAiSummary.scss';
import { Scrollbars } from "react-custom-scrollbars";
import React, { useCallback, useMemo } from "react";

const Scrollbar: FCX = ({
    children,
}) => {
    return (
        <Scrollbars
            renderThumbVertical={(props) => (
                <div {...props} className="HomeCategoryReportsAiSummary__scrollbar-thumb"/>
            )}
            renderView={(props) => (
                <div {...props} className="HomeCategoryReportsAiSummary__scrollbar-view"/>
            )}
        >
            <>{children}</>
        </Scrollbars>
    );
};

const HomeCategoryReportsAiSummary: FCX<{
    categoryName: string;
}> = ({
    categoryName,
}) => {
    const { userInfo: { reportProDetailsUrl, products } } = usePlatformContext();

    const {
        data: {
            answer,
            items,
        },
        isLoading,
    } = useHomeCategoryReportsSummary(categoryName);

    const reportPro = useMemo(
        () => products[AppId.ReportPro],
        [products]
    );

    const getItemLink = useCallback(
        (id: any) => sanitize(`${reportPro.url}${reportProDetailsUrl}${id}`),
        [reportPro.url, reportProDetailsUrl]
    );

    const answerWithLinks = useMemo(
        () => {
            const div = document.createElement('div');
            div.innerHTML = answer ?? '';

            const cites = div.querySelectorAll('cite');
            cites.forEach(i => {
                const id = i.getAttribute('data-citation-id');
                if (id) {
                    const link = getItemLink(id);
                    const a = document.createElement('a');
                    a.href = link;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    a.innerHTML = i.innerHTML;
                    i.innerText = '';
                    i.appendChild(a);
                }
            });

            return div.innerHTML;
        },
        [answer, getItemLink]
    );

    const sourceItems = useMemo(
        () => {
            const div = document.createElement('div');
            div.innerHTML = answer ?? '';

            const cites = div.querySelectorAll('cite');
            const sourceItemsIds = Array.from(cites).map(i => i.getAttribute('data-citation-id'));

            return (items ?? []).filter(
                i => sourceItemsIds.some(
                    id => equalInLC(`${i.id}`, id ?? '')
                )
            );
        },
        [answer, items]
    );

    return (
        <div className="HomeCategoryReportsAiSummary">
            <HomeSectionHeader className="no-pt">
                <HomeSectionSubTitle>Category Snapshot: {categoryName}</HomeSectionSubTitle>
            </HomeSectionHeader>
            <AiResultContainer className="HomeCategoryReportsAiSummary__ai-result">
                {isLoading && (
                    <div className="HomeCategoryReportsAiSummary__ai-answer is-skeleton">
                        <Skeleton height={40}/>
                        <Skeleton height={40} style={{ width: '70%', }}/>
                        <Skeleton height={40}/>
                        <Skeleton height={40} style={{ width: '30%', }}/>
                        <Skeleton height={40}/>
                        <Skeleton height={40} style={{ width: '70%', }}/>
                        <Skeleton height={40}/>
                    </div>
                )}
                {!isLoading && (
                    <div className="HomeCategoryReportsAiSummary__ai-answer">
                        <Scrollbar>
                            <div
                                className="HomeCategoryReportsAiSummary__html-content html"
                                dangerouslySetInnerHTML={{ __html: answerWithLinks ?? "" }}
                            />
                        </Scrollbar>
                    </div>
                )}
                <div className="HomeCategoryReportsAiSummary__divider"/>
                <div className="HomeCategoryReportsAiSummary__sources">
                    <Scrollbar>
                        <div className="HomeCategoryReportsAiSummary__sources-title">
                            Sources
                        </div>
                        <div className="HomeCategoryReportsAiSummary__sources-items">

                            {isLoading && (new Array(2).fill(null).map((_, index) => (
                                <div
                                    key={index}
                                    className="HomeCategoryReportsAiSummarySource"
                                >
                                    <div className="HomeCategoryReportsAiSummarySource__image">
                                        <Skeleton/>
                                    </div>
                                    <div className="HomeCategoryReportsAiSummarySource__content">
                                        <section>
                                            <Skeleton className="HomeCategoryReportsAiSummarySource__title"/>
                                            <Skeleton className="HomeCategoryReportsAiSummarySource__text" width={150}/>
                                        </section>
                                    </div>
                                </div>
                            )))}
                            {!isLoading && sourceItems.map((i) => {
                                const url = getItemLink(i.id);

                                return (
                                    <div className="HomeCategoryReportsAiSummarySource">
                                        <div className="HomeCategoryReportsAiSummarySource__image">
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                <img src={i.imageUrl ?? ''} alt={i.title ?? ''}/>
                                            </a>
                                        </div>
                                        <div className="HomeCategoryReportsAiSummarySource__content">
                                            <section>
                                                <div className="HomeCategoryReportsAiSummarySource__title">
                                                    <a
                                                        href={url}
                                                        className="HomeCategoryReportsAiSummarySource__title"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {i.title}
                                                    </a>
                                                </div>
                                                <div className="HomeCategoryReportsAiSummarySource__text">
                                                    published {i.createDate}
                                                </div>
                                            </section>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Scrollbar>
                </div>
            </AiResultContainer>
        </div>
    );
}

export default HomeCategoryReportsAiSummary;